import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  clearCommentState,
  createCommunityCommentThunk,
  deleteCommunityCommentThunk,
  deleteCommunityPostThunk,
  fetchCommunityCommentsThunk,
  fetchCommunityPostThunk,
  followCommunityAuthorThunk,
  likeCommunityCommentThunk,
  likeCommunityPostThunk,
  toggleSavePost,
  unfollowCommunityAuthorThunk,
  unlikeCommunityCommentThunk,
  unlikeCommunityPostThunk,
} from '../../redux/communitySlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { AsyncStatus } from '../../redux/communitySlice';
import type { CommunityLayoutOutletContext } from '../layout/CommunityLayout';
import CommunityPostCard from '../../components/CommunityPostCard';
import type { CommunityPost, CommunityTopic } from '../../types';
import { fetchCommunityPosts } from '../../services/communityService';
import './PostDetailPage.css';

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSearchInput } = useOutletContext<CommunityLayoutOutletContext>();
  const {
    currentPost,
    currentPostStatus,
    currentPostError,
    comments,
    topics,
    feedItems,
    savedPostIds,
  } = useAppSelector((state) => state.community);
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? null;

  const [likePending, setLikePending] = useState(false);
  const [followPending, setFollowPending] = useState(false);
  const [recommendations, setRecommendations] = useState<CommunityPost[]>([]);
  const [recommendStatus, setRecommendStatus] = useState<AsyncStatus>('idle');
  const [recommendError, setRecommendError] = useState<string | null>(null);

  useEffect(() => {
    setSearchInput('');
  }, [setSearchInput]);

  useEffect(() => {
    if (!postId) {
      return;
    }
    dispatch(fetchCommunityPostThunk(postId));
    dispatch(fetchCommunityCommentsThunk({ postId }));
    return () => {
      dispatch(clearCommentState(postId));
    };
  }, [dispatch, postId]);

  const topicsDict = useMemo(() => {
    return topics.reduce<Record<string, CommunityTopic>>((acc, topic) => {
      acc[topic.id] = topic;
      return acc;
    }, {});
  }, [topics]);

  const detailPost = useMemo(() => {
    if (!postId) {
      return null;
    }
    if (currentPost && currentPost.post_id === postId) {
      return currentPost;
    }
    return feedItems.find((item) => item.post_id === postId) ?? null;
  }, [postId, currentPost, feedItems]);

  useEffect(() => {
    if (!postId || !detailPost) {
      return;
    }
    const primaryTopic = detailPost.topics[0] ?? null;
    if (!primaryTopic) {
      setRecommendations(
        feedItems.filter((item) => item.post_id !== postId).slice(0, 5),
      );
      setRecommendStatus('succeeded');
      return;
    }
    setRecommendStatus('loading');
    setRecommendError(null);
    fetchCommunityPosts({ topic: primaryTopic, limit: 6, sort: 'trending' })
      .then((response) => {
        const items = response.items.filter((item) => item.post_id !== postId);
        setRecommendations(items);
        setRecommendStatus('succeeded');
      })
      .catch((error) => {
        setRecommendError(error instanceof Error ? error.message : '加载推荐内容失败');
        setRecommendStatus('failed');
      });
  }, [postId, detailPost, feedItems]);

  const commentEntry = postId ? comments[postId] : undefined;
  const commentList = commentEntry?.items ?? [];
  const commentStatus: AsyncStatus = commentEntry?.status ?? 'idle';
  const commentError = commentEntry?.error ?? null;
  const commentCreateStatus: AsyncStatus = commentEntry?.createStatus ?? 'idle';
  const commentCreateError = commentEntry?.createError ?? null;
  const commentLikePending = commentEntry?.likePending ?? {};

  const handleSubmitComment = async (content: string, parentCommentId?: string | null) => {
    if (!postId) {
      return;
    }
    await dispatch(
      createCommunityCommentThunk({
        postId,
        payload: {
          content,
          parent_comment_id: parentCommentId ?? undefined,
        },
      }),
    ).unwrap();
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!postId) {
      return;
    }
    await dispatch(deleteCommunityCommentThunk({ postId, commentId })).unwrap();
  };

  const handleToggleCommentLike = async (commentId: string, hasLiked: boolean) => {
    if (!postId) {
      return;
    }
    const thunk = hasLiked ? unlikeCommunityCommentThunk : likeCommunityCommentThunk;
    await dispatch(thunk({ postId, commentId })).unwrap();
  };

  const handleDeletePost = async () => {
    if (!postId) {
      return;
    }
    await dispatch(deleteCommunityPostThunk(postId)).unwrap();
  navigate('/app/community');
  };

  const handleToggleLike = async (hasLiked: boolean) => {
    if (!postId || likePending) {
      return;
    }
    setLikePending(true);
    try {
      if (hasLiked) {
        await dispatch(unlikeCommunityPostThunk(postId)).unwrap();
      } else {
        await dispatch(likeCommunityPostThunk(postId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setLikePending(false);
    }
  };

  const handleToggleFollow = async (authorId: number, followsAuthor: boolean) => {
    if (followPending) {
      return;
    }
    setFollowPending(true);
    try {
      if (followsAuthor) {
        await dispatch(unfollowCommunityAuthorThunk(authorId)).unwrap();
      } else {
        await dispatch(followCommunityAuthorThunk(authorId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setFollowPending(false);
    }
  };

  const handleOpenRecommendation = (id: string) => {
  navigate(`/app/community/status/${id}`);
  };

  if (!postId) {
    return <div className="community-detail__placeholder">未找到对应的帖子。</div>;
  }

  if (!detailPost && currentPostStatus === 'loading') {
    return <div className="community-detail__placeholder">帖子加载中...</div>;
  }

  if (!detailPost && currentPostStatus === 'failed') {
    return (
      <div className="community-detail__placeholder">
        {currentPostError || '帖子加载失败，请稍后再试。'}
      </div>
    );
  }

  if (!detailPost) {
    return <div className="community-detail__placeholder">帖子不存在或已被删除。</div>;
  }

  const viewer = detailPost.viewer ?? {};
  const authorId = detailPost.author?.id;
  const hasSaved = viewer.has_saved ?? savedPostIds.includes(detailPost.post_id);

  const handleToggleSave = () => {
    dispatch(toggleSavePost(detailPost));
  };

  const handleStartRepost = () => {
  navigate(`/app/community/compose?repost=${detailPost.post_id}`);
  };

  return (
    <div className="community-detail">
      <section className="community-detail__post">
        <button type="button" className="community-detail__back" onClick={() => navigate(-1)}>
          ← 返回上一页
        </button>
        <div className="community-detail__actions">
          <button type="button" className="community-detail__action-button" onClick={handleStartRepost}>
            转发
          </button>
        </div>
        <CommunityPostCard
          post={detailPost}
          topicsDict={topicsDict}
          comments={commentList}
          commentsStatus={commentStatus}
          commentsError={commentError}
          createStatus={commentCreateStatus}
          createError={commentCreateError}
          onLoadComments={() => dispatch(fetchCommunityCommentsThunk({ postId }))}
          onSubmitComment={handleSubmitComment}
          onDeleteComment={handleDeleteComment}
          onToggleCommentLike={handleToggleCommentLike}
          commentLikePending={commentLikePending}
          currentUserId={userId}
          onDeletePost={detailPost.permissions?.can_edit ? handleDeletePost : undefined}
          onToggleLike={() => handleToggleLike(viewer.has_liked ?? false)}
          likePending={likePending}
          onToggleFollow={authorId ? () => handleToggleFollow(authorId, viewer.follows_author ?? false) : undefined}
          followPending={authorId ? followPending : undefined}
          defaultExpanded
          hasSaved={hasSaved}
          onToggleSave={handleToggleSave}
          onOpenRepost={(id) => navigate(`/app/community/status/${id}`)}
        />
      </section>

      <aside className="community-detail__aside">
        <section className="community-detail__panel">
          <header>
            <h2>相关讨论</h2>
          </header>
          {recommendStatus === 'loading' && (
            <div className="community-detail__placeholder">推荐内容加载中...</div>
          )}
          {recommendStatus === 'failed' && recommendError && (
            <div className="community-detail__error">{recommendError}</div>
          )}
          {recommendations.length === 0 && recommendStatus === 'succeeded' && (
            <div className="community-detail__placeholder">暂无推荐内容，换个话题试试吧。</div>
          )}
          {recommendations.length > 0 && (
            <ul className="community-detail__recommendations">
              {recommendations.slice(0, 5).map((item) => (
                <li key={item.post_id}>
                  <article>
                    <button type="button" onClick={() => handleOpenRecommendation(item.post_id)}>
                      {item.title}
                    </button>
                    <span>{item.author?.name || item.author?.username || '匿名用户'}</span>
                    <time>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '刚刚'}</time>
                  </article>
                </li>
              ))}
            </ul>
          )}
        </section>
      </aside>
    </div>
  );
};

export default PostDetailPage;
